import type { NewsPreviewCardFragment } from '@/lib/types/datocms/graphql/generated'
import { linkResolver } from '@/utils/datocms/links'
import { formatDate } from '@/utils/formatDate'
import Image from 'next/image'
import Link from 'next/link'
import Eyebrow from './ui/Eyebrow'
import Arrow from './svg/icon/Arrow'
import clsx from 'clsx'

export default function NewsPreviewCard({
	__typename,
	title,
	coverImage,
	publishDate,
	slug,
	subCategories,
	className,
}: Readonly<NewsPreviewCardFragment> & { className?: string }) {
	return (
		<article
			className={clsx(
				'news-preview-card carousel-card group relative rounded-xl overflow-hidden bg-white',
				className
			)}
		>
			<Link
				href={linkResolver({ __typename, slug })}
				aria-label={title ?? 'News Article'}
				className="absolute inset-0 z-10"
			/>
			{coverImage?.[0]?.url && (
				<div className="relative h-56 overflow-hidden bg-white-frosted">
					<Image
						src={coverImage?.[0]?.url}
						alt={coverImage?.[0]?.alt ?? 'News Image'}
						width={250}
						height={250}
						className="w-full h-full object-cover group-hover:scale-[103%] transition-transform duration-500 m-ease"
						sizes="(max-width: 768px) 456px, 366px"
					/>
				</div>
			)}

			<div className="flex flex-col p-6 lg:py-6 lg:px-8 h-72">
				<div className="flex items-center justify-between">
					{subCategories && subCategories.length > 0 && (
						<Eyebrow className="uppercase z-0">
							{subCategories.map((subCategory) => subCategory.title).join(', ')}
						</Eyebrow>
					)}
					{publishDate && (
						<time className="font-mono text-sm text-blue-dark/60" dateTime={publishDate}>
							{formatDate(publishDate)}
						</time>
					)}
				</div>

				{title && <h3 className="text-xl font-medium mt-7 line-clamp-4 leading-tight">{title}</h3>}

				<div className="flex items-center justify-between mt-auto border-t pt-5 group-hover:text-blue">
					<span className="flex text-xs font-semibold uppercase group-hover:text-blue transition-colors duration-300 ease-out">
						Read More
					</span>

					<Arrow />
				</div>
			</div>
		</article>
	)
}
